import { JSX, ReactNode, useRef, type KeyboardEvent } from "react";
import { Radio } from "./components/Radio";

import { Text, Color, Flex, InfoTooltip } from "src/elements";

interface Props<T> {
  options: { label: string; value: T; isDisabled?: boolean }[];
  value: T;
  setValue: (value: T) => void;
  label?: string;
  /** The group's name for a screen reader when the visible title sits outside `Radios`. */
  ariaLabel?: string;
  tooltip?: ReactNode;
  width?: string;
  direction?: "column" | "row";
  size?: "default" | "small";
  isUnselectable?: boolean;
  variant?: "body2" | "small2";
  isDisabled?: boolean;
  optionsCustomRender?: () => JSX.Element | null;
}

const NEXT_KEYS = ["ArrowRight", "ArrowDown"];
const PREVIOUS_KEYS = ["ArrowLeft", "ArrowUp"];

/**
 * A radio group: one tab stop (the checked option, else the first enabled one), the arrow keys
 * move to and pick the next or previous enabled option, Space and Enter pick the focused one.
 */
export const Radios = <T,>({
  options,
  value,
  setValue,
  label,
  ariaLabel,
  tooltip,
  width = "100%",
  direction = "row",
  size = "default",
  isUnselectable = false,
  isDisabled = false,
  variant,
  optionsCustomRender,
}: Props<T>) => {
  const refOptions = useRef<(HTMLDivElement | null)[]>([]);

  const isEnabled = (index: number) => !isDisabled && !options[index]?.isDisabled;
  const checkedIndex = options.findIndex((option) => option.value === value);
  const tabStop =
    checkedIndex >= 0 && isEnabled(checkedIndex)
      ? checkedIndex
      : options.findIndex((_, index) => isEnabled(index));

  const onKeyDown = (from: number) => (event: KeyboardEvent<HTMLDivElement>) => {
    const step = NEXT_KEYS.includes(event.key) ? 1 : PREVIOUS_KEYS.includes(event.key) ? -1 : 0;
    if (!step || isDisabled) return;
    event.preventDefault();
    const count = options.length;
    for (let offset = 1; offset < count; offset++) {
      const index = (((from + step * offset) % count) + count) % count;
      if (!isEnabled(index)) continue;
      refOptions.current[index]?.focus();
      if (options[index].value !== value) setValue(options[index].value);
      return;
    }
  };

  return (
    <Flex width={width} justify="between" align="center" gap="10px" direction={direction}>
      {label && (
        <Flex>
          <Text
            variant={size === "small" ? "caption1" : "body1"}
            color={
              (isDisabled && Color.textDisabled) ||
              (size === "small" && Color.primaryActive) ||
              Color.textMain
            }
          >
            {label}
          </Text>

          {tooltip && <InfoTooltip tooltip={tooltip} />}
        </Flex>
      )}

      <Flex
        gap="5px 10px"
        direction={direction}
        role="radiogroup"
        aria-label={ariaLabel ?? label}
        aria-disabled={isDisabled || undefined}
      >
        {options.map((option, i) => (
          <Radio
            key={i}
            label={option.label}
            value={option.value}
            setIsChecked={setValue}
            size={size}
            variant={variant}
            isChecked={option.value === value}
            isDisabled={isDisabled || option.isDisabled}
            isUnselectable={isUnselectable}
            tabIndex={i === tabStop ? 0 : -1}
            onKeyDown={onKeyDown(i)}
            refOption={(element) => {
              refOptions.current[i] = element;
            }}
          />
        ))}

        {optionsCustomRender && options.find((o) => o.value === value) && (
          <Flex margin="10px" justify="center">
            {optionsCustomRender()}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
